
import { ModelBase } from "@models/UpsertableBase";
import { Feature } from "@models";
import { v4 as uuidv4 } from "uuid";
import { AbilityScoreExpandedType, AttackType, CreatureFBType, DataType } from "@dndModels/Types";
import { ASIBaseFeature } from "@dndModels";
import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

/**
 * This is equivalent to each box in the race or class pages.
 * It contains lists of Features.
 */
export class FeatureBase {
  // These 3 don't go to the db.  They're just for identifying within the UI.
  parent_type = "";
  parent_id = "";

  true_id = "";
  _name = "";
  _description = "";
  use_single_ff = true;

  get fb_name() {
    if (this.use_single_ff) {
      if (this.single_ff) {
        return this.single_ff.use_name;
      } else if (this.features.filter(o => !!o.floating_feature).length > 0) {
        const ff_names = common_util.unique_array(this.features.map(o => o.name));
        if (ff_names.length === 1) {
          return ff_names[0];
        } else {
          // Check for a name that is shorter than the others, 
          // and which the others include as part of them.
          // If found, use that.
          const lengths = ff_names.map(o => o.length);
          const min = Math.min(...lengths);
          const shortests = ff_names.filter(o => o.length === min);
          if (shortests.length === 1) {
            const shortest = shortests[0];
            if (ff_names.every(o => o.includes(shortest))) {
              return shortest;
            }
          }
        }
      }
    }
    return this._name;
  }
  set fb_name(value: string) {
    this._name = value;
  }
  get description() {
    if (this.use_single_ff && this.single_ff && this.repeat_levels.length === 0) {
      return this.single_ff.description;
    }
    return this._description;
  }
  set description(value: string) {
    this._description = value;
  }

  feature_source_type = ""; // For everything except Feature Sources, this is the type of Feature Source
  feature_source_id = ""; // For everything except Feature Sources, this is the id of the Feature Source

  for_class = "Default"; // if "Default" then do default.  if "Char" then do char level.  else it should be a class id
  level = 1; // Level of when it is available.  0 for ones that don't have a requirement. 
  position_at_level = 0; // Not implemented in the UI yet, but I'm going to use this for sorting within the same level.
  repeat_levels: number[] = []; // This is so I can put a single fb for Ability Score Improvement/Feat at level 4 on classes, and specify the levels it gets repeated.
  features: Feature[] = [];
  prevent_duplicate_choices = false;
  multiclassing = 0; 
  // Default 0 means unaffected by multiclassing
  // 1 means only on first class
  // 2 means only on classes after first
  required_condition_ids: string[] = [];
  exclude_conditions = false;
  condition_level = 1;
  min_spell_slot_level = 0;

  display = true;
  replaces_feature_base_id = "";
  replaces_feature_base: FeatureBase | null = null;
  upgrades_feature_base_id = "";
  upgrades_feature_base: FeatureBase | null = null;
  key = "";
  replacement_key = "";
  replace_key = false;
  creature_fb_type: CreatureFBType | "" = "";

  get single_feature() {
    return this.features.length === 1 ? this.features[0] : undefined;
  }

  get single_ff() {
    const single_f = this.single_feature;
    return single_f ? single_f.floating_feature : undefined;
  }

  constructor(obj?: any, is_new = false) {
    this.true_id = obj && obj.true_id && !is_new ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.fb_name = obj.name ? `${obj.name}` : "";
      this.fb_name = this.fb_name.trim();
      this.description = obj.description ? `${obj.description}` : "";
      this.description = this.description.trim();
      
      this.feature_source_type = obj.feature_source_type ? obj.feature_source_type : (obj.source_type ? obj.source_type : "");
      this.feature_source_id = obj.feature_source_id ? obj.feature_source_id : (obj.source_id ? obj.source_id : "");
      this.parent_type = this.feature_source_type;
      this.parent_id = this.feature_source_id;
      
      this.for_class = obj.for_class ? obj.for_class : "Default";
      this.level = obj.level !== undefined ? +obj.level : 1;
      this.position_at_level = obj.position_at_level !== undefined ? obj.position_at_level : 0;
      this.repeat_levels = obj.repeat_levels ? obj.repeat_levels : [];
      
      this.features = [];
      if (obj.features && obj.features.length) {
        obj.features.forEach((f: any) => {
          const feature = new Feature(f); // this.common_util.new_feature(f);
          feature.parent_type = this.parent_type;
          feature.parent_id = this.parent_id;
          feature.base_id = this.id;
          this.features.push(feature);
        });
      }
      this.use_single_ff = obj.use_single_ff !== undefined ? obj.use_single_ff : true;
      
      this.prevent_duplicate_choices = obj.prevent_duplicate_choices ? obj.prevent_duplicate_choices : false;
      this.multiclassing = obj.multiclassing ? obj.multiclassing : 0;
      this.required_condition_ids = obj.required_condition_ids ? obj.required_condition_ids : [];
      this.required_condition_ids = this.required_condition_ids.filter(o => o !== "ALL" && o !== "All");
      this.exclude_conditions = obj.exclude_conditions ? obj.exclude_conditions : false;
      this.condition_level = obj.condition_level ? obj.condition_level : 1;
      this.min_spell_slot_level = obj.min_spell_slot_level ? obj.min_spell_slot_level : 0;
      this.display = obj.display !== undefined ? obj.display : true;
      this.replaces_feature_base_id = obj.replaces_feature_base_id ? obj.replaces_feature_base_id : "";
      this.upgrades_feature_base_id = obj.upgrades_feature_base_id ? obj.upgrades_feature_base_id : "";
      this.key = obj.key ? obj.key : "";
      this.replacement_key = obj.replacement_key ? obj.replacement_key : "";
      this.replace_key = obj.replace_key ? obj.replace_key : false;
      if (this.fb_name.includes(" Of ")) {
        this.fb_name = this.fb_name.replace(" Of ", " of ");
      }
      this.creature_fb_type = obj.creature_fb_type ? obj.creature_fb_type : "";
    }
  }

  get feature_types() {
    return this.features.flatMap(o => o.feature_type);
  }

  get ability_scores(): AbilityScoreExpandedType[] {
    const asis = this.features.filter(o => o.floating_feature instanceof ASIBaseFeature).map(o => o.floating_feature) as ASIBaseFeature[];
    return asis.flatMap(o => o.choices.flatMap(o2 => o2.asi_features.flatMap(o3 => o3.options)));
  }

  get asi_min(): number {
    const asis = this.features.filter(o => o.floating_feature instanceof ASIBaseFeature).map(o => o.floating_feature) as ASIBaseFeature[];
    const amounts = asis.flatMap(o => o.choices.flatMap(o2 => o2.asi_features.flatMap(o3 => o3.amount)));
    return amounts.length > 0 ? Math.max(...amounts) : 0;
  }

  get the_effect_types() {
    return this.features.flatMap(o => o.the_effect_types);
  }

  get the_attack_types() {
    return this.features.flatMap(o => o.the_attack_types);
  }

  get damage_types() {
    return this.features.flatMap(o => o.damage_types);
  }

  get the_utility_details(): string[] {
    return this.features.flatMap(o => o.the_utility_details);
  }

  get id(): string {
    return this.true_id;
  }

  get total_feature_points(): number {
    return this.features.reduce((accumulator: number, fp: Feature) => {
      return accumulator + fp.feature_points;
    }, 0);
  }

  get total_feature_points_from_admin(): number {
    return this.features.reduce((accumulator: number, fp: Feature) => {
      return accumulator + fp.feature_points_from_admin;
    }, 0);
  }

  get total_avg_feature_points(): number {
    return this.features.reduce((accumulator: number, fp: Feature) => {
      return accumulator + fp.avg_feature_points;
    }, 0);
  }

  toDBObj(): any {
    const features = this.features.map(feature => feature.toDBObj());
    // console.log(features);
    return {
      true_id: this.true_id,
      name: this.fb_name,
      description: this.description,
      feature_source_type: this.feature_source_type,
      feature_source_id: this.feature_source_id,
      for_class: this.for_class,
      level: this.level,
      position_at_level: this.position_at_level,
      repeat_levels: this.repeat_levels,
      features,
      use_single_ff: this.use_single_ff,
      prevent_duplicate_choices: this.prevent_duplicate_choices,
      multiclassing: this.multiclassing,
      required_condition_ids: this.required_condition_ids,
      exclude_conditions: this.exclude_conditions,
      condition_level: this.condition_level,
      min_spell_slot_level: this.min_spell_slot_level,
      display: this.display,
      replaces_feature_base_id: this.replaces_feature_base_id,
      upgrades_feature_base_id: this.upgrades_feature_base_id,
      key: this.key,
      replacement_key: this.replacement_key,
      replace_key: this.replace_key,
      creature_fb_type: this.creature_fb_type
    };
  }

  copy(copyMe: FeatureBase, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.fb_name = copyMe.fb_name;
    this.description = copyMe.description;
    this.feature_source_type = copyMe.feature_source_type;
    this.feature_source_id = copyMe.feature_source_id;
    this.for_class = copyMe.for_class;
    this.level = copyMe.level;
    this.position_at_level = copyMe.position_at_level;
    this.repeat_levels = [...copyMe.repeat_levels];
    this.features = copyMe.features.map(f => f.clone(drop_ids));
    this.use_single_ff = copyMe.use_single_ff;
    this.prevent_duplicate_choices = copyMe.prevent_duplicate_choices;
    this.multiclassing = copyMe.multiclassing;
    this.required_condition_ids = [...copyMe.required_condition_ids];
    this.exclude_conditions = copyMe.exclude_conditions;
    this.condition_level = copyMe.condition_level;
    this.min_spell_slot_level = copyMe.min_spell_slot_level;
    this.display = copyMe.display;
    if (!drop_ids) {
      this.replaces_feature_base_id = copyMe.replaces_feature_base_id;
      this.replaces_feature_base = copyMe.replaces_feature_base;
      this.upgrades_feature_base_id = copyMe.upgrades_feature_base_id;
      this.upgrades_feature_base = copyMe.upgrades_feature_base;
    }
    this.key = copyMe.key;
    this.replacement_key = copyMe.replacement_key;
    this.replace_key = copyMe.replace_key;
    this.creature_fb_type = copyMe.creature_fb_type;
  }

  clone(drop_ids = false): FeatureBase {
    const new_obj = new FeatureBase();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class OptionalFeatureBase extends ModelBase {
  data_type: DataType = "optional_feature_base";
  feature_base: FeatureBase = new FeatureBase();
  // source_books: { type: string, id: string, third_party: boolean }[] = [];
  // source_book_type: string = "";
  // source_book_id: string = "";
  // feature_source_type: string = "";
  feature_source_id = ""; // If it's an OKFB then this will be ""
  get fb_name() {
    return this.name;
  }
  set fb_name(value: string) {
    this.name = value;
  }
  
  constructor(obj?: any) {
    super(obj);
    if (obj) { 
      this.feature_base = obj.feature_base ? new FeatureBase(obj.feature_base) : new FeatureBase();
      
      if (this.source_books_old.length === 0) {
        this.source_books_old.push({ type: "Source Book", id: "60d9e2ff909a1d2014235f15", third_party: false });
      }
      // this.feature_source_type = obj.feature_source_type ? obj.feature_source_type : "";
      this.feature_source_id = obj.feature_source_id ? obj.feature_source_id : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   this.feature_base = obj.feature_base ? new FeatureBase(obj.feature_base) : new FeatureBase();
  // }

  get the_effect_types(): string[] {
    return this.features.flatMap(o => o.the_effect_types);
  }

  get the_attack_types(): AttackType[] {
    return this.features.flatMap(o => o.the_attack_types);
  }

  get source_book_ids(): string[] {
    return this.source_books_old.map(o => { return o.id; });
  }

  get source_book_types() {
    return this.source_books_old.map(o => { return o.type; });
  }

  get id(): string {
    return this._id;
  }
  get features(): Feature[] {
    return this.feature_base.features;
  }
  get parent_type(): string {
    return this.feature_base.parent_type;
  }
  get parent_id(): string {
    return this.feature_base.parent_id;
  }
  get level(): number {
    return this.feature_base.level;
  }
  get position_at_level() {
    return this.feature_base.position_at_level;
  }
  get repeat_levels() {
    return this.feature_base.repeat_levels;
  }
  get for_class(): string {
    return this.feature_base.for_class;
  }
  get prevent_duplicate_choices(): boolean {
    return this.feature_base.prevent_duplicate_choices;
  }
  get multiclassing(): number {
    return this.feature_base.multiclassing;
  }
  get required_condition_ids(): string[] {
    return this.feature_base.required_condition_ids;
  }
  get exclude_conditions() {
    return this.feature_base.exclude_conditions;
  }
  get condition_level(): number {
    return this.feature_base.condition_level;
  }
  get min_spell_slot_level() {
    return this.feature_base.min_spell_slot_level;
  }
  get display(): boolean {
    return this.feature_base.display;
  }
  get replaces_feature_base_id(): string {
    return this.feature_base.replaces_feature_base_id;
  }
  get replaces_feature_base(): FeatureBase | null {
    return this.feature_base.replaces_feature_base;
  }
  get upgrades_feature_base_id(): string {
    return this.feature_base.upgrades_feature_base_id;
  }
  get upgrades_feature_base(): FeatureBase | null {
    return this.feature_base.upgrades_feature_base;
  }

  get key(): string {
    return this.feature_base.key;
  }

  get replacement_key(): string {
    return this.feature_base.replacement_key;
  }

  get replace_key(): boolean {
    return this.feature_base.replace_key;
  }
  
  set features(value: Feature[]) {
    this.feature_base.features = value;
  }
  set parent_type(value: string) {
    this.feature_base.parent_type = value;
  }
  set parent_id(value: string) {
    this.feature_base.parent_id = value;
  }
  set level(value: number) {
    this.feature_base.level = value;
  }
  set position_at_level(value: number) {
    this.feature_base.position_at_level = value;
  }
  set repeat_levels(value: number[]) {
    this.feature_base.repeat_levels = value;
  }
  set prevent_duplicate_choices(value: boolean) {
    this.feature_base.prevent_duplicate_choices = value;
  }
  set multiclassing(value: number) {
    this.feature_base.multiclassing = value;
  }
  set required_condition_ids(value: string[]) {
    this.feature_base.required_condition_ids = value;
  }
  set exclude_conditions(value: boolean) {
    this.feature_base.exclude_conditions = value;
  }
  set condition_level(value: number) {
    this.feature_base.condition_level = value;
  }
  set min_spell_slot_level(value: number) {
    this.feature_base.min_spell_slot_level = value;
  }
  set display(value: boolean) {
    this.feature_base.display = value;
  }
  set replaces_feature_base_id(value: string) {
    this.feature_base.replaces_feature_base_id = value;
  }
  set replaces_feature_base(value: FeatureBase | null) {
    this.feature_base.replaces_feature_base = value;
  }
  set upgrades_feature_base_id(value: string) {
    this.feature_base.upgrades_feature_base_id = value;
  }
  set upgrades_feature_base(value: FeatureBase | null) {
    this.feature_base.upgrades_feature_base = value;
  }

  set replacement_key(value: string) {
    this.feature_base.replacement_key = value;
  }

  set replace_key(value: boolean) {
    this.feature_base.replace_key = value;
  }

  get total_feature_points(): number {
    return this.features.reduce((accumulator: number, fp: Feature) => {
      return accumulator + fp.feature_points;
    }, 0);
  }

  get total_feature_points_from_admin(): number {
    return this.features.reduce((accumulator: number, fp: Feature) => {
      return accumulator + fp.feature_points_from_admin;
    }, 0);
  }

  get total_avg_feature_points(): number {
    return this.features.reduce((accumulator: number, fp: Feature) => {
      return accumulator + fp.avg_feature_points;
    }, 0);
  }

  toDBObj(): any {
    const base_obj = super.toDBObj();
    return {
      ...base_obj,
      feature_base: this.feature_base.toDBObj(),
      source_books: this.source_books_old,
      // feature_source_type: this.feature_source_type === "" ? this.feature_base.feature_source_type : this.feature_source_type,
      feature_source_id: this.feature_source_id === "" ? this.feature_base.feature_source_id : this.feature_source_id
    };
  }

  toLight(): any {
    // if (this.name.includes("Darkvision")) {
    //   debugger;
    // }
    return {
      ...super.toLight(),
      source_books: this.source_books_old,
      feature_source_id: this.feature_source_id === "" ? this.feature_base.feature_source_id : this.feature_source_id
    };
  }

  copy(copyMe: OptionalFeatureBase, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.feature_base = copyMe.feature_base.clone(drop_ids);
    this.source_books_old = [];
    copyMe.source_books_old.forEach(sb => {
      this.source_books_old.push({...sb});
    });
    // this.feature_source_type = copyMe.feature_source_type;
    this.feature_source_id = copyMe.feature_source_id;
  }

  clone(drop_ids = false): OptionalFeatureBase {
    const new_obj = new OptionalFeatureBase();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  useFeatureBase(copyMe: FeatureBase): void {
    this.name = copyMe.fb_name;
    this.description = copyMe.description;
    // this.feature_source_type = copyMe.feature_source_type;
    this.feature_source_id = copyMe.feature_source_id;
    this.level = copyMe.level;
    this.position_at_level = copyMe.position_at_level;
    this.repeat_levels = [...copyMe.repeat_levels];
    this.features = copyMe.features;
    this.prevent_duplicate_choices = copyMe.prevent_duplicate_choices;
    this.multiclassing = copyMe.multiclassing;
    this.required_condition_ids = [...copyMe.required_condition_ids];
    this.exclude_conditions = copyMe.exclude_conditions;
    this.condition_level = copyMe.condition_level;
    this.min_spell_slot_level = copyMe.min_spell_slot_level;
    this.display = copyMe.display;
    this.replaces_feature_base_id = copyMe.replaces_feature_base_id;
    this.replaces_feature_base = copyMe.replaces_feature_base;
    this.upgrades_feature_base_id = copyMe.upgrades_feature_base_id;
    this.upgrades_feature_base = copyMe.upgrades_feature_base;
  }
}
